/**
 * Songs Analyzer Page - Subscriber access to Stream Monitor
 * 
 * This page provides access to station statistics for subscribers
 * with the "Complete Package". Unlike the admin Stream Monitor,
 * users only see their assigned stations without admin controls.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { 
  ArrowLeft, Radio, Calendar, Download, Clock, 
  Music, TrendingUp, AlertCircle, Mail, Play, Loader2, Info,
  BarChart3, Sun, Sunset, Moon, Users, Activity, List, Trophy, Layers, X, Flame
} from 'lucide-react';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';

// Import existing Stream Monitor components
import SongTable from '../components/StreamMonitor/SongTable';
import PeriodSelector from '../components/StreamMonitor/PeriodSelector';
// v2.9.203 — "Νέες Προσθήκες" tab removed at operator request (showed
// hundreds of songs without day-control). Both the tab and the
// underlying NewEntriesTable import are gone. The component file
// itself is intentionally kept in the repo for git history; nothing
// references it anymore.
import DaypartLegend from '../components/StreamMonitor/DaypartLegend';
import PlaylistChartView from '../components/StreamMonitor/PlaylistChartView';
import SongsAnalyzerListenerReport from '../components/SongsAnalyzerListenerReport';
import DataQualityBadge from '../components/DataQualityBadge';
import TopArtistsLeaderboard from '../components/TopArtistsLeaderboard';
import BroadcastTimeline from '../components/BroadcastTimeline';
import PrefectureReport from '../components/PrefectureReport';
import StreamUrlSubmission from '../components/SongsAnalyzer/StreamUrlSubmission';
import BurnTab from '../components/SongsAnalyzer/BurnTab';
import PerformanceScoreSection from '../components/PerformanceScoreSection';
import EraChart from '../components/EraChart';

// Daypart icons mapping
const DAYPART_ICONS = {
  morning: Sun,
  midday: Sun,
  afternoon: Sunset,
  drive: Sunset,
  night: Moon
};

// Data Mode Badge Component with tooltip
const DataModeBadge = ({ dataMode, t }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const isLive = dataMode === 'live';
  
  const tooltipText = isLive 
    ? (t ? t('liveAudienceTooltip') : 'Τα metrics βασίζονται σε πραγματικούς concurrent listeners από τον streaming server του σταθμού.\nΗ ανάλυση αποτυπώνει την πραγματική αντίδραση του κοινού σε κάθε τραγούδι.')
    : (t ? t('songsAnalyzerMetricsNote') : 'Stream Analytics mode');
  
  return (
    <div 
      className="relative inline-flex items-center gap-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip(!showTooltip)}
    >
      <span 
        className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
          isLive 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-blue-500/20 text-blue-400'
        }`}
      >
        {isLive ? 'Live Audience' : 'Stream Analytics'}
      </span>
      <Info className="w-3 h-3 text-white/40 cursor-help" />
      
      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="absolute z-50 bottom-full left-0 mb-2 w-64 p-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg whitespace-pre-line border border-white/10"
          style={{ minWidth: '250px' }}
        >
          <div className={`font-semibold mb-1 ${isLive ? 'text-green-400' : 'text-blue-400'}`}>
            {isLive ? '📡 Live Audience' : '📊 Stream Analytics'}
          </div>
          {tooltipText}
          <div className="absolute bottom-0 left-4 transform translate-y-full">
            <div className="border-8 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const SongsAnalyzerPage = ({ user }) => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const backendUrl = getBackendUrl();
  const { playStation, currentStation, isPlaying, isLoading: playerLoading } = useRadioPlayer();
  
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [period, setPeriod] = useState('yesterday');
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('stats'); // v2.9.203: 'new' tab removed; remaining: 'stats', 'dayparts', 'artists', 'timeline', 'music', 'era', 'pro', 'prefecture'
  const [daypartsData, setDaypartsData] = useState(null);
  const [daypartsLoading, setDaypartsLoading] = useState(false);

  // ─── Stale-response guard (v2.9.11) ───────────────────────────────
  // User confirmed via 5 production screenshots that ANEMOS 95,6 SAMOS
  // displayed 1055 Rock's songs+counts (195 songs / 215 plays — same
  // numbers as 1055 Rock) in the "Σύνολο" tab while Dayparts/Timeline
  // showed the correct Greek artists. Root cause: in production
  // (~3.7s per stats request), switching stations races — fetchStats
  // for the previous station resolves AFTER the new one and overwrites
  // setStats() with the old payload.
  //
  // This is the SAFE version of v2.9.8 (which was rolled back because
  // it called setStats(null) and made the table go blank for 4s during
  // any switch). Here we:
  //   1. Tag every in-flight request with the station id it was for.
  //   2. On response, compare to activeStationIdRef.current — if the
  //      user has moved on, drop the payload (console.warn but no UI
  //      change, so the previous station's table stays visible until
  //      the NEW station's response arrives a moment later — exactly
  //      what the user asked for after the v2.9.8 rollback).
  //   3. Do NOT setStats(null) on switch — keep the previous data
  //      visible during the network round-trip.
  const activeStationIdRef = useRef(null);

  
  // Pro Analytics state
  const [hasProAnalytics, setHasProAnalytics] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Listener Stats state
  const [hasListenerStats, setHasListenerStats] = useState(false);

  // Era filter (set by clicking a decade/year on the Era Chart tab).
  // When non-null, the "Σύνολο" tab filters the song list to that era.
  // Shape: { type: 'decade' | 'year', value: '2020s' | 2025, label: string }
  const [eraFilter, setEraFilter] = useState(null);

  const handleEraSelect = (selection) => {
    setEraFilter(selection);
    setActiveTab('stats');
  };

  // Apply the era filter to the songs list (decade range or exact year).
  const filteredStats = useMemo(() => {
    if (!eraFilter || !stats?.songs) return stats;
    const songs = stats.songs.filter(s => {
      const yr = s.release_year;
      if (eraFilter.type === 'year') {
        return yr && parseInt(yr, 10) === eraFilter.value;
      }
      // Decade filter — match the decade key e.g. '2020s'
      if (eraFilter.type === 'decade') {
        if (eraFilter.value === 'unknown') return !yr;
        if (eraFilter.value === 'earlier') return yr && parseInt(yr, 10) < 1960;
        const startYear = parseInt(eraFilter.value, 10); // '2020s' → 2020
        return yr && parseInt(yr, 10) >= startYear && parseInt(yr, 10) < startYear + 10;
      }
      return true;
    });
    return { ...stats, songs };
  }, [stats, eraFilter]);

  // Fetch user's assigned stations
  useEffect(() => {
    if (user?.user_id || user?.id) {
      fetchMyStations();
    }
  }, [user]);

  // Fetch stats when station or period changes
  useEffect(() => {
    if (selectedStation) {
      // v2.9.11 stale-response guard: register the current station id
      // BEFORE the fetch. Late responses for the previous station will
      // see a different ref and self-discard inside fetchStats/fetchDayparts.
      // NOTE: we deliberately do NOT clear stats/daypartsData here —
      // that was the v2.9.8 mistake (caused a 4s blank UI on prod).
      activeStationIdRef.current = selectedStation.id;

      fetchStats(selectedStation.id, period);
      // Fetch dayparts data if not "today" (dayparts only for completed days)
      if (period !== 'today') {
        fetchDayparts(selectedStation.id, period);
      } else {
        setDaypartsData(null);
      }
    }
  }, [selectedStation, period]);

  const fetchMyStations = async () => {
    try {
      const userId = user?.user_id || user?.id;
      const response = await fetch(
        `${backendUrl}/api/songs-analyzer/my-stations?user_id=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const data = await response.json();
      
      if (response.status === 403) {
        setHasAccess(false);
        setError(data.detail || t('noAccessSongsAnalyzer'));
        setLoading(false);
        return;
      }
      
      setHasAccess(true);
      setStations(data.stations || []);
      
      // If stationId in URL, select that station
      if (stationId && data.stations) {
        const station = data.stations.find(s => s.id === stationId);
        if (station) {
          setSelectedStation(station);
        }
      } else if (data.stations?.length >= 1) {
        // Always auto-select first station (for admin or users with stations)
        setSelectedStation(data.stations[0]);
        navigate(`/songs-analyzer/${data.stations[0].id}`, { replace: true });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stations:', err);
      setLoading(false);
    }
  };

  const fetchStats = async (stationId, period) => {
    try {
      setLoading(true);
      setStatsError(false);
      const userId = user?.user_id || user?.id;
      const response = await fetch(
        `${backendUrl}/api/songs-analyzer/station/${stationId}/stats?user_id=${userId}&period=${period}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      const data = await response.json();

      // v2.9.11 stale-response guard: drop late responses from a
      // station the user has already moved away from. Prevents
      // cross-station data bleed (the production 195 songs / 215
      // plays bug where ANEMOS displayed 1055 Rock's payload).
      if (activeStationIdRef.current !== stationId) {
        console.warn(
          `[stats] dropped stale response for ${stationId} ` +
          `(active station is ${activeStationIdRef.current})`
        );
        // Do NOT touch setStats — leave the current (fresh) data alone.
        return;
      }

      setStats(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stats:', err);
      if (activeStationIdRef.current === stationId) setStatsError(true);
      setLoading(false);
    }
  };

  const fetchDayparts = async (stationId, period) => {
    try {
      setDaypartsLoading(true);
      const userId = user?.user_id || user?.id;
      const response = await fetch(
        `${backendUrl}/api/songs-analyzer/station/${stationId}/dayparts?user_id=${userId}&period=${period}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        // Don't show error for today - dayparts not available
        if (period === 'today') {
          setDaypartsData(null);
          setDaypartsLoading(false);
          return;
        }
        throw new Error('Failed to fetch dayparts');
      }
      
      const data = await response.json();

      // v2.9.11 stale-response guard (same rationale as fetchStats).
      if (activeStationIdRef.current !== stationId) {
        console.warn(
          `[dayparts] dropped stale response for ${stationId} ` +
          `(active station is ${activeStationIdRef.current})`
        );
        return;
      }

      setDaypartsData(data);
      setDaypartsLoading(false);
    } catch (err) {
      console.error('Error fetching dayparts:', err);
      setDaypartsData(null);
      setDaypartsLoading(false);
    }
  };

  // Check Pro Analytics & Listener Stats access when station changes
  useEffect(() => {
    if (selectedStation) {
      checkProAnalyticsAccess(selectedStation.id);
      checkListenerStatsAccess(selectedStation.id);
    }
  }, [selectedStation]);

  const checkProAnalyticsAccess = async (stationId) => {
    try {
      const userId = user?.user_id || user?.id;
      const response = await fetch(
        `${backendUrl}/api/songs-analyzer/check-pro-analytics?user_id=${userId}&station_id=${stationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHasProAnalytics(data.has_access);
      }
    } catch (err) {
      console.error('Error checking pro analytics access:', err);
      setHasProAnalytics(false);
    }
  };

  const checkListenerStatsAccess = async (stationId) => {
    try {
      const userId = user?.user_id || user?.id;
      const response = await fetch(
        `${backendUrl}/api/songs-analyzer/check-listener-stats?user_id=${userId}&station_id=${stationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHasListenerStats(data.has_access);
      }
    } catch (err) {
      console.error('Error checking listener stats access:', err);
      setHasListenerStats(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedStation || exportLoading) return;
    
    setExportLoading(true);
    const userId = user?.user_id || user?.id;
    const url = `${backendUrl}/api/songs-analyzer/station/${selectedStation.id}/export/xlsx?period=${period}&user_id=${userId}`;
    
    const attemptDownload = (retryCount = 0) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `songs-analyzer-${selectedStation.name || selectedStation.id}-${period}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
          toast.success('Το αρχείο εξήχθη επιτυχώς!');
          setExportLoading(false);
        } else if (xhr.status === 429 && retryCount < 2) {
          const delay = (retryCount + 1) * 3000;
          toast.info(`Υπερβολικά πολλά αιτήματα, επαναπροσπάθεια σε ${delay/1000}s...`);
          setTimeout(() => attemptDownload(retryCount + 1), delay);
        } else {
          toast.error(`Σφάλμα κατά την εξαγωγή (${xhr.status})`);
          setExportLoading(false);
        }
      };
      
      xhr.onerror = function() {
        if (retryCount < 2) {
          setTimeout(() => attemptDownload(retryCount + 1), 3000);
        } else {
          toast.error('Σφάλμα σύνδεσης κατά την εξαγωγή');
          setExportLoading(false);
        }
      };
      
      xhr.send();
    };
    
    attemptDownload();
  };

  // No access view - Welcome message for users without access
  if (!hasAccess && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#16213e] py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-8"
          >
            <ArrowLeft size={20} />
            Επιστροφή
          </button>
          
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center border border-purple-500/20">
            {/* Header */}
            <div className="mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Songs Analyzer
              </h1>
              <p className="text-purple-300">
                Εργαλείο ανάλυσης μουσικού περιεχομένου ραδιοφώνου
              </p>
            </div>
            
            {/* Features */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/5 rounded-xl p-4">
                <Music className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold mb-1">Στατιστικά Τραγουδιών</h3>
                <p className="text-white/60 text-sm">Δείτε τα top τραγούδια του σταθμού σας</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold mb-1">Retention Analysis</h3>
                <p className="text-white/60 text-sm">Μετρήστε την αντίδραση των ακροατών</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold mb-1">Ιστορικά Δεδομένα</h3>
                <p className="text-white/60 text-sm">Αναλύστε τάσεις και πατέρν</p>
              </div>
            </div>
            
            {/* Call to Action */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-6 mb-6 border border-purple-500/30">
              <h2 className="text-xl font-semibold text-white mb-3">
                Θέλετε πρόσβαση;
              </h2>
              <p className="text-white/70 mb-4">
                Επικοινωνήστε μαζί μας για να ενεργοποιήσουμε δωρεάν δοκιμαστική περίοδο 
                ή επιλέξτε ένα από τα πακέτα συνδρομής μας.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-lg transition-colors"
                >
                  <Mail size={18} />
                  Επικοινωνία
                </a>
                <a
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20"
                >
                  Πακέτα Συνδρομής
                </a>
              </div>
            </div>
            
            <p className="text-white/50 text-sm">
              Εάν έχετε ήδη συνδρομή και δεν βλέπετε τον σταθμό σας, 
              επικοινωνήστε με την υποστήριξη.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Handle Listen Live button
  const handleListenLive = () => {
    if (!selectedStation) return;
    
    playStation({
      _id: selectedStation.id,
      name: selectedStation.name,
      stream_url: selectedStation.stream_url
    });
  };

  // Check if this station is currently playing
  const isCurrentStationPlaying = currentStation?._id === selectedStation?.id && isPlaying;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#16213e] py-3 px-3 sm:px-4 pb-20">
      {/* SEO Meta Tags - Title inherited from index.html (Radio Solutions Apps) */}
      <Helmet>
        <meta name="description" content="Songs Analyzer - Αναλυτικά στοιχεία για τα τραγούδια που παίζουν στους ραδιοφωνικούς σταθμούς. Μεταδόσεις, TLH, retention και daypart analytics." />
        <meta name="keywords" content="songs analyzer, radio analytics, ανάλυση τραγουδιών, ραδιοφωνικά στατιστικά, plays, TLH, retention, daypart, radio solutions" />
        <link rel="canonical" href="https://radio-apps.online/songs-analyzer" />
        <meta property="og:description" content="Songs Analyzer - Αναλυτικά στοιχεία για τα τραγούδια που παίζουν στους ραδιοφωνικούς σταθμούς." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto">
        {/* Header - Compact Professional Layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          {/* Title Row */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/70 hover:text-white p-1"
            >
              <ArrowLeft size={18} />
            </button>
            <Radio className="w-5 h-5 text-[#FF9F0A]" />
            <div>
              <h1 className="text-base sm:text-lg font-bold text-white">Songs Analyzer</h1>
              <div className="flex items-center gap-2">
                <p className="text-white/50 text-xs">
                  {selectedStation?.name || t('loading')}
                </p>
                {/* Data Mode Badge */}
                {stats && (
                  <DataModeBadge dataMode={stats.data_mode || 'stream'} t={t} />
                )}
              </div>
            </div>
          </div>
          
          {/* Controls Row: Listen Live + Station Selector */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Listen Live Button */}
            {selectedStation?.stream_url && (
              <button
                onClick={handleListenLive}
                disabled={playerLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition-all text-sm
                  ${isCurrentStationPlaying 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                    : 'bg-[#FF9F0A] hover:bg-[#FF8C00] text-white'
                  }`}
                data-testid="listen-live-btn"
              >
                {playerLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Play className="w-3.5 h-3.5" />
                )}
                {isCurrentStationPlaying ? 'On Air' : 'Listen Live'}
              </button>
            )}
            
            {/* Station selector */}
            {(user?.role === 'admin' || stations.length > 1) && selectedStation && (
              <select
                value={selectedStation?.id || ''}
                onChange={(e) => {
                  if (!e.target.value) return;
                  const station = stations.find(s => s.id === e.target.value);
                  setSelectedStation(station);
                  navigate(`/songs-analyzer/${e.target.value}`);
                }}
                className="bg-white/10 text-white border border-white/20 rounded-lg px-2 py-1.5 text-sm w-full sm:w-auto"
              >
                {stations.map(s => (
                  <option key={s.id} value={s.id} className="bg-gray-800">
                    {s.name}
                  </option>
                ))}
              </select>
            )}

            {/* Stream URL Submission - admin + assigned users */}
            {selectedStation && (
              <StreamUrlSubmission
                stationId={selectedStation.id}
                stationName={selectedStation.name}
                apiBase={getBackendUrl()}
                token={localStorage.getItem('token')}
              />
            )}
          </div>
        </div>

        {/* Only show content if station is selected */}
        {selectedStation && (
          <>
        {/* Data Quality Badge — tells user how reliable analytics are */}
        <div className="flex flex-wrap items-center gap-2 -mt-1 mb-2">
          <DataQualityBadge
            stationId={selectedStation.id}
            token={localStorage.getItem('token')}
            showDetails={false}
          />
        </div>
        {/* Period Selector & Actions - Compact Row */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {activeTab === 'burn' ? (
            <span className="text-sm text-amber-300/80" data-testid="burn-period-note">{t('burnPeriodNote')}</span>
          ) : (
            <PeriodSelector 
              value={period}
              onChange={setPeriod}
            />
          )}
          
          <button
            onClick={handleExportExcel}
            disabled={exportLoading}
            data-testid="export-excel-btn"
            className={`flex items-center gap-1.5 ${exportLoading ? 'bg-green-800 cursor-wait' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1.5 rounded-lg transition-colors text-sm`}
          >
            {exportLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exportLoading ? 'Εξαγωγή...' : 'Excel'}
          </button>
        </div>

        {/* Stats Summary - Compact Professional Layout */}
        {stats && (
          <div className="flex flex-wrap items-stretch gap-3 mb-4">
            {/* Stats Pills */}
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-lg px-4 py-2">
              <Music size={16} className="text-blue-400" />
              <span className="text-white/50 text-sm">{t('saSongs')}</span>
              <span className="text-xl font-bold text-white">{stats.unique_songs || 0}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-lg px-4 py-2">
              <TrendingUp size={16} className="text-green-400" />
              <span className="text-white/50 text-sm">{t('saBroadcasts')}</span>
              <span className="text-xl font-bold text-white">{stats.total_plays || 0}</span>
            </div>
          </div>
        )}

        {/* Tabs: Στατιστικά / Dayparts / Νέες Προσθήκες / Νομός */}
        {(selectedStation || activeTab === 'prefecture') && (
          <div className="flex gap-1 mb-3 flex-wrap">
            {selectedStation && (
              <>
                <button
                  onClick={() => setActiveTab('stats')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'stats' 
                      ? 'bg-[#FF9F0A] text-black' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  <BarChart3 size={14} />
                  {t('saTabAll')}
                </button>
                <button
                  onClick={() => setActiveTab('dayparts')}
                  disabled={period === 'today'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'dayparts' 
                      ? 'bg-[#FF9F0A] text-black' 
                      : period === 'today'
                        ? 'bg-white/5 text-white/30 cursor-not-allowed'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                  title={period === 'today' ? 'Dayparts διαθέσιμο μόνο για ολοκληρωμένες ημέρες' : ''}
                >
                  <Clock size={14} />
                  Dayparts
                </button>

                {/* v2.9.203 — "Νέες Προσθήκες" tab removed (showed
                    hundreds of entries with no day-control filter). */}

                {/* Top Artists tab */}
                <button
                  onClick={() => setActiveTab('artists')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'artists'
                      ? 'bg-[#FF9F0A] text-black'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                  data-testid="tab-top-artists"
                >
                  <Music size={14} />
                  Top Artists
                </button>

                {/* Broadcast Timeline tab */}
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'timeline'
                      ? 'bg-[#FF9F0A] text-black'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                  data-testid="tab-timeline"
                >
                  <List size={14} />
                  Timeline
                </button>

                {/* Music Performance tab — programming-side metrics
                    (frequency vs national avg, music variety, vs previous period)
                    Moved out of "Στατιστικά Ακροατών" so the audience panel
                    contains only audience-related numbers. */}
                <button
                  onClick={() => setActiveTab('music-performance')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'music-performance'
                      ? 'bg-amber-500 text-black'
                      : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/40'
                  }`}
                  data-testid="tab-music-performance"
                  title="Πόσο συχνά παίζεις, ποικιλία μουσικής, σύγκριση με προηγούμενη εβδομάδα"
                >
                  <Trophy size={14} />
                  Music Performance
                </button>

                {/* Era Chart tab — decade/year breakdown of programming.
                    Click on any decade/year navigates back to "Σύνολο" with a
                    pre-applied era filter. */}
                <button
                  onClick={() => setActiveTab('era-chart')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'era-chart'
                      ? 'bg-amber-500 text-black'
                      : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/40'
                  }`}
                  data-testid="tab-era-chart"
                  title="Κατανομή προγράμματος ανά δεκαετία και έτος"
                >
                  <Layers size={14} />
                  Era Chart
                </button>

                {/* Burn tab — airplay overexposure (spins history), reliable for every station. */}
                <button
                  onClick={() => setActiveTab('burn')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'burn'
                      ? 'bg-amber-500 text-black'
                      : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/40'
                  }`}
                  data-testid="tab-burn"
                  title="Υπερέκθεση τραγουδιών (Burn) από το ιστορικό παιξιμάτων"
                >
                  <Flame size={14} />
                  Burn
                </button>

                {/* Pro Analytics Button */}
                <button
                  onClick={() => {
                    if (!hasProAnalytics) {
                      setShowProModal(true);
                    } else {
                      setActiveTab('pro');
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'pro' 
                      ? 'bg-emerald-500 text-white' 
                      : hasProAnalytics
                        ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/50'
                        : 'bg-white/5 text-white/40 hover:bg-white/10 border border-white/10'
                  }`}
                  title="Λεπτομερής προβολή μεταδόσεων: Chart, Airplay Log, Dayparts (όπως το Stream Monitor)"
                >
                  <BarChart3 size={14} />
                  Pro Analytics
                  {!hasProAnalytics && <span className="text-xs ml-1 opacity-60"></span>}
                </button>
              </>
            )}
            <button
              data-testid="prefecture-tab-btn"
              onClick={() => setActiveTab('prefecture')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'prefecture' 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 border border-indigo-500/50'
              }`}
            >
              <Users size={14} />
              {t('saTabPrefecture')}
            </button>
          </div>
        )}

        {/* Prefecture tab - always accessible */}
        {!selectedStation && activeTab !== 'prefecture' && (
          <div className="flex gap-1 mb-3">
            <button
              data-testid="prefecture-tab-btn-standalone"
              onClick={() => setActiveTab('prefecture')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 border border-indigo-500/50"
            >
              <Users size={14} />
              {t('saTabPrefecture')}
            </button>
          </div>
        )}

        {/* Pro Analytics Access Modal */}
        {showProModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl max-w-md w-full p-6 border border-gray-700">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">📊 Pro Analytics</h3>
                <p className="text-gray-400">
                  Η λειτουργία Pro Analytics δεν είναι ενεργοποιημένη για αυτόν τον σταθμό.
                </p>
              </div>
              
              <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-300 mb-3">
                  Με το Pro Analytics αποκτάτε πρόσβαση σε:
                </p>
                <ul className="text-sm text-gray-400 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">✓</span>
                    Ωριαία κατανομή μεταδόσεων
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">✓</span>
                    Ημερολόγιο αναπαραγωγών (grid view)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">✓</span>
                    Ιστορικό μεταδόσεων ανά τραγούδι
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400">✓</span>
                    Λεπτομερείς αναφορές
                  </li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowProModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Κλείσιμο
                </button>
                <a
                  href="https://radio-apps.online/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors text-center font-medium"
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Επικοινωνία
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'stats' ? (
          <>
            {/* Era Filter Badge — shows when user clicked a decade/year on Era Chart */}
            {eraFilter && (
              <div
                className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500/15 border border-amber-500/40 rounded-lg"
                data-testid="era-filter-badge"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Layers className="w-4 h-4 text-amber-400" />
                  <span className="text-white/70">Φίλτρο εποχής:</span>
                  <span className="font-semibold text-amber-300">{eraFilter.label}</span>
                  {filteredStats?.songs && (
                    <span className="text-xs text-white/50">
                      ({filteredStats.songs.length} από {stats?.songs?.length || 0} τραγούδια)
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setEraFilter(null)}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white/80 transition-colors"
                  data-testid="era-filter-clear"
                >
                  <X className="w-3 h-3" />
                  Καθαρισμός
                </button>
              </div>
            )}

            {/* Song Table - Stats Tab */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9F0A]"></div>
              </div>
            ) : filteredStats?.songs?.length > 0 ? (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
                <SongTable 
                  stats={filteredStats}
                  loading={loading}
                  stationId={selectedStation?.id}
                  period={period}
                  isAdmin={false}
                  userId={user?.user_id || user?.id}
                />
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-12 text-center">
                <Music className="w-16 h-16 text-white/30 mx-auto mb-4" />
                {statsError ? (
                  <div data-testid="stats-load-error">
                    <p className="text-amber-300/90 mb-4">Η φόρτωση άργησε ή απέτυχε. Τα δεδομένα δεν χάθηκαν.</p>
                    <button
                      data-testid="stats-retry-btn"
                      onClick={() => fetchStats(selectedStation?.id, period)}
                      className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm transition-colors"
                    >
                      Δοκιμή ξανά
                    </button>
                  </div>
                ) : (
                  <p className="text-white/50">
                    {eraFilter
                      ? `Δεν βρέθηκαν τραγούδια για το φίλτρο «${eraFilter.label}». Δοκίμασε άλλη εποχή ή καθάρισε το φίλτρο.`
                      : t('noDataForPeriodSongs')}
                  </p>
                )}
              </div>
            )}
            
            {/* Listener Stats - Below song table (admin always sees; assigned users see their station's stats too) */}
            {selectedStation && (
              <SongsAnalyzerListenerReport
                stationId={selectedStation.id}
                stationName={selectedStation.name}
                userId={user?.user_id || user?.id}
                token={localStorage.getItem('token')}
              />
            )}
          </>
        ) : activeTab === 'dayparts' ? (
          /* Dayparts Tab */
          <div className="space-y-6">
            {period === 'today' ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
                <Clock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-amber-300 font-medium">Η ανάλυση Dayparts είναι διαθέσιμη μόνο για ολοκληρωμένες ημέρες.</p>
                <p className="text-white/50 text-sm mt-2">{t('selectYesterdayForDaypart')}</p>
              </div>
            ) : daypartsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9F0A]"></div>
              </div>
            ) : daypartsData?.dayparts?.length > 0 ? (
              <>
                {/* Daypart Metrics Guide - ONLY for Daypart view */}
                <DaypartLegend currentDaypart={null} />
                
                {/* Dayparts Summary */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    {daypartsData.dayparts.map((dp) => {
                      const DpIcon = DAYPART_ICONS[dp.key] || Clock;
                      return (
                        <div key={dp.key} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                          <DpIcon size={16} className="text-[#FF9F0A]" />
                          <span className="text-white/70 text-sm">{dp.label}</span>
                          <span className="text-white font-bold">{dp.total_plays}</span>
                          <span className="text-white/40 text-xs">plays</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Daypart Sections */}
                {daypartsData.dayparts.map((daypart) => {
                  const DpIcon = DAYPART_ICONS[daypart.key] || Clock;
                  return (
                    <div key={daypart.key} className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
                      {/* Section Header */}
                      <div className="px-4 py-3 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <DpIcon size={20} className="text-[#FF9F0A]" />
                            <h3 className="text-white font-semibold">
                              {daypart.name} ({daypart.time_range})
                            </h3>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-white/50">
                              <span className="text-white font-medium">{daypart.total_plays}</span> plays
                            </span>
                            <span className="text-white/50">
                              <span className="text-white font-medium">{daypart.unique_songs}</span> τραγούδια
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Songs Table for this daypart */}
                      {/* Live Audience: shows Avg Listeners */}
                      {/* Stream Analytics: hides Avg Listeners, shows Activity Index */}
                      {daypart.songs?.length > 0 ? (
                        <SongTable 
                          stats={{
                            songs: daypart.songs,
                            total_plays: daypart.total_plays,
                            unique_songs: daypart.unique_songs,
                            total_tlh: daypart.total_tlh,
                            // CRITICAL: Use data_mode from daypartsData response
                            // This respects the station's mode (Live Audience vs Stream Analytics)
                            data_mode: daypartsData?.data_mode || stats?.data_mode || 'stream',
                            // Live Audience: show_listeners = true (shows Avg Listeners in dayparts too)
                            // Stream Analytics: show_listeners = false (NEVER shows Avg Listeners)
                            show_listeners: daypartsData?.is_live_audience || false
                          }}
                          loading={false}
                          stationId={selectedStation?.id}
                          period={period}
                          isAdmin={false}
                          userId={user?.user_id || user?.id}
                          // isDaypartView=true means: TIME FILTER ONLY - same metrics as main view
                          isDaypartView={true}
                        />
                      ) : (
                        <div className="p-8 text-center text-white/40">
                          {t('noDataForDaypart')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-12 text-center">
                <Clock className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <p className="text-white/50">
                  {t('noDataDayparts')}
                </p>
              </div>
            )}
          </div>
        ) : activeTab === 'artists' ? (
          /* Top Artists Leaderboard — uses previous full week benchmark */
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 overflow-hidden">
            <TopArtistsLeaderboard
              stationId={selectedStation?.id}
              token={localStorage.getItem('token')}
              limit={25}
            />
          </div>
        ) : activeTab === 'timeline' ? (
          /* Broadcast Timeline — chronological event log for a single day */
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 overflow-hidden">
            <BroadcastTimeline
              stationId={selectedStation?.id}
              token={localStorage.getItem('token')}
            />
          </div>
        ) : activeTab === 'music-performance' ? (
          /* Music Performance — programming metrics (frequency vs national avg,
             music variety, vs previous period). Promoted from a sub-tab inside
             "Στατιστικά Ακροατών" to a top-level tab for clearer IA. */
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 overflow-hidden" data-testid="music-performance-tab-content">
            <PerformanceScoreSection
              stationId={selectedStation?.id}
              token={localStorage.getItem('token')}
            />
          </div>
        ) : activeTab === 'burn' ? (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 overflow-hidden" data-testid="burn-tab-wrapper">
            <BurnTab stationId={selectedStation?.id} />
          </div>
        ) : activeTab === 'era-chart' ? (
          /* Era Chart — nested decade/year horizontal bars with Plays/Variety
             toggle. Click on a bar filters the "Σύνολο" tab via eraFilter state. */
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 overflow-hidden" data-testid="era-chart-tab-content">
            <EraChart
              songs={stats?.songs || []}
              onEraSelect={handleEraSelect}
            />
          </div>
        ) : activeTab === 'pro' ? (
          /* Pro Analytics Tab - Uses PlaylistChartView from Stream Monitor */
          <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
            {hasProAnalytics ? (
              <PlaylistChartView 
                stationId={selectedStation?.id}
                stationName={selectedStation?.name}
                onClose={() => setActiveTab('stats')}
                embedded={true}
                initialPeriod={period}
              />
            ) : (
              <div className="text-center py-12 text-white/50">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Δεν έχετε πρόσβαση στο Pro Analytics για αυτόν τον σταθμό</p>
              </div>
            )}
          </div>
        ) : activeTab === 'prefecture' ? (
          /* Prefecture Tab - inline in the chain */
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 overflow-hidden">
            <PrefectureReport 
              user={user}
              dateFrom={period === 'today' ? new Date().toISOString().split('T')[0] : 
                        period === 'yesterday' ? new Date(Date.now() - 86400000).toISOString().split('T')[0] :
                        period === 'last7' ? new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0] :
                        period === 'last30' ? new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0] :
                        null}
              dateTo={period === 'yesterday' ? new Date(Date.now() - 86400000).toISOString().split('T')[0] :
                      new Date().toISOString().split('T')[0]}
            />
          </div>
        ) : null /* v2.9.203 — Νέες Προσθήκες tab removed */}

        {/* Request More Stations */}
        <div className="mt-8 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">
            {t('saMoreStations')}
          </h3>
          <p className="text-white/70 mb-4">
            {t('saMoreStationsDesc')}
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Mail size={18} />
            {t('saContact')}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SongsAnalyzerPage;
